/**
 * JBL alternative Stella FAB
 *
 * - Restyles the Stella "Lass dich beraten" floating action button inside its
 *   open shadow root (matches the JBL design: white bg, 1px black border,
 *   13px / weight 300, no leading logo, integrated close X on the right).
 * - Injects a close button as a child of the Open Actions button. Clicking it
 *   minimizes the FAB to a small square showing only the JACKS Signet
 *   (persisted in sessionStorage). The minimized button still opens Stella's
 *   consultation panel on click via Stella's native open-actions handler.
 * - Scroll-based visibility: button appears at 30% scroll depth on non-PDP
 *   pages, or after the ".product-usp-wrapper" section on PDPs.
 *
 * Gated by the theme setting `enable_alternative_stella_fab`. When that toggle
 * is off this script is not loaded, and Stella's stock styling is preserved.
 */

(function () {
  'use strict';

  var MINIMIZED_KEY = 'jbl-stella-fab-minimized';
  var VISIBLE_CLASS = 'is-jbl-visible';
  var MINIMIZED_CLASS = 'is-jbl-minimized';

  /* Matches the theme's existing close icon shape (snippets/global-icon.liquid
     case 'close'): two crossed diagonal lines, currentColor, plain stroke. */
  var CLOSE_SVG = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true"><line x1="1" y1="1" x2="11" y2="11"/><line x1="11" y1="1" x2="1" y2="11"/></svg>';

  var STELLA_CSS = [
    'button[aria-label="Open actions"] {',
    '  background: #ffffff !important;',
    '  background-color: #ffffff !important;',
    '  background-image: none !important;',
    '  border: 1px solid #111111 !important;',
    '  border-color: #111111 !important;',
    '  border-width: 1px !important;',
    '  border-radius: 2px !important;',
    '  box-shadow: 0 4px 20px rgba(0,0,0,0.10) !important;',
    '  font-size: 13px !important;',
    '  font-weight: 300 !important;',
    '  letter-spacing: normal !important;',
    '  padding: 11px 0 11px 15px !important;',
    '  padding-right: 0 !important;',
    '  z-index: 1000 !important;',
    '  color: #111111 !important;',
    '  isolation: isolate !important;',
    /* Slide-in is driven by the host's .is-jbl-visible class. Always carry an
       explicit `translate3d(...)` so the browser interpolates between two
       3d-matrices (smooth) instead of between translateY → none (jumpy). The
       3d form also forces a GPU layer to remove flicker. */
    '  transform: translate3d(0, 0, 0) !important;',
    '  transition: transform 0.35s ease, background-color 0.2s ease !important;',
    '  will-change: transform !important;',
    '  backface-visibility: hidden !important;',
    '}',
    ':host(:not(.' + VISIBLE_CLASS + ')) button[aria-label="Open actions"] {',
    '  transform: translate3d(0, 12px, 0) !important;',
    '}',
    /* Hide Stella's leading logo in the default (expanded) state */
    ':host(:not(.' + MINIMIZED_CLASS + ')) button[aria-label="Open actions"] img,',
    ':host(:not(.' + MINIMIZED_CLASS + ')) button[aria-label="Open actions"] strong > img {',
    '  display: none !important;',
    '}',
    /* Injected close button (only shown in expanded state).
       Spacing: text → 10px → | (separator) → 14px → X → 14px → edge. */
    '[data-jbl-close] {',
    '  display: inline-flex !important;',
    '  align-items: center !important;',
    '  justify-content: center !important;',
    '  align-self: stretch !important;',
    '  margin-left: 10px !important;',
    '  padding: 0 14px !important;',
    '  border: 0 !important;',
    '  border-left: 1px solid #cccccc !important;',
    '  background: transparent !important;',
    '  color: #999999 !important;',
    '  cursor: pointer !important;',
    '  transition: color 0.2s ease !important;',
    '}',
    '[data-jbl-close]:hover { color: #111111 !important; }',
    '[data-jbl-close] svg {',
    '  width: 12px !important;',
    '  height: 12px !important;',
    '  display: block !important;',
    '  pointer-events: none !important;',
    '}',
    /* ---------- Minimized state (after close X clicked) ---------- */
    /* Shrink button to a square showing only the JACKS Signet. Click on it
       still triggers Stella\'s native open-actions behaviour, so the user can
       reopen the consultation panel. */
    ':host(.' + MINIMIZED_CLASS + ') button[aria-label="Open actions"] {',
    '  padding: 8px !important;',
    '  gap: 0 !important;',
    '  width: 44px !important;',
    '  height: 44px !important;',
    '  min-width: 0 !important;',
    '  justify-content: center !important;',
    '}',
    ':host(.' + MINIMIZED_CLASS + ') button[aria-label="Open actions"] span,',
    ':host(.' + MINIMIZED_CLASS + ') button[aria-label="Open actions"] strong {',
    '  display: none !important;',
    '}',
    ':host(.' + MINIMIZED_CLASS + ') [data-jbl-close] {',
    '  display: none !important;',
    '}',
    ':host(.' + MINIMIZED_CLASS + ') button[aria-label="Open actions"] img {',
    '  display: block !important;',
    '  width: 20px !important;',
    '  height: 20px !important;',
    '  object-fit: contain !important;',
    '}',
    /* Stella\'s actions popup (the menu that opens on click). Stella applies
       `stella-fab-popup .fixed > .absolute { bottom: calc(var(--spacing) * 23) }`
       (~5.75rem). Override with the same path so specificity matches; ours
       comes after in the adoptedStyleSheets order so it wins the cascade. */
    'stella-fab-popup .fixed > .absolute,',
    '.absolute.bottom-20.right-8 {',
    '  bottom: 7rem !important;',
    '}',
    /* On mobile + PDP push the popup higher because the sticky ATC eats the
       lower screen real estate. Host gets `.is-jbl-pdp` class from JS when
       template-product is detected. */
    '@media (max-width: 767px) {',
    '  :host(.is-jbl-pdp) stella-fab-popup .fixed > .absolute,',
    '  :host(.is-jbl-pdp) .absolute.bottom-20.right-8 {',
    '    bottom: 8rem !important;',
    '  }',
    '}',
    ''
  ].join('\n');

  /* Host-level CSS lives inline in snippets/html-bottom-js.liquid so it's
     applied before Stella mounts the element (eliminates first-paint flash).
     This script only handles shadow-root styles, close button, visibility. */

  function injectShadowStyles(root) {
    /* 1. Constructable Stylesheets — preferred, sits at the end of
          adoptedStyleSheets so it wins the cascade against Stella's own. */
    try {
      if ('adoptedStyleSheets' in root && typeof CSSStyleSheet !== 'undefined') {
        var sheet = new CSSStyleSheet();
        sheet.replaceSync(STELLA_CSS);
        root.adoptedStyleSheets = root.adoptedStyleSheets.concat([sheet]);
      }
    } catch (e) {}
    /* 2. Fallback <style> appended at the end of the shadow root. */
    if (!root.querySelector('[data-jbl-stella-style]')) {
      var style = document.createElement('style');
      style.setAttribute('data-jbl-stella-style', '');
      style.textContent = STELLA_CSS;
      root.appendChild(style);
    }
  }

  function ensureCloseButton(root, hostEl) {
    var openBtn = root.querySelector('button[aria-label="Open actions"]');
    if (!openBtn) return;
    if (openBtn.querySelector('[data-jbl-close]')) return; // already injected

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.setAttribute('data-jbl-close', '');
    closeBtn.setAttribute('aria-label', 'Beratungs-Button schließen');
    closeBtn.innerHTML = CLOSE_SVG;
    closeBtn.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      try { sessionStorage.setItem(MINIMIZED_KEY, '1'); } catch (err) {}
      hostEl.classList.add(MINIMIZED_CLASS);
    });
    openBtn.appendChild(closeBtn);
  }

  function restoreMinimizedState(hostEl) {
    try {
      if (sessionStorage.getItem(MINIMIZED_KEY) === '1') {
        hostEl.classList.add(MINIMIZED_CLASS);
      }
    } catch (e) {}
  }

  function setupVisibility(hostEl) {
    var isVisible = false;
    function setVisible(next) {
      if (next === isVisible) return;
      isVisible = next;
      hostEl.classList.toggle(VISIBLE_CLASS, next);
    }

    var isPDP = document.body.classList.contains('template-product');
    var pdpTrigger = isPDP ? document.querySelector('.product-usp-wrapper') : null;
    if (isPDP) hostEl.classList.add('is-jbl-pdp');

    function thirtyPercentScrolled() {
      var max = document.documentElement.scrollHeight - window.innerHeight;
      if (max <= 0) return true;
      return (window.scrollY / max) >= 0.30;
    }

    function computeNext() {
      if (isPDP && pdpTrigger) {
        /* Show once the USP section's top has scrolled into the upper 40%
           of the viewport — i.e. about 60% of the section is in view. */
        return pdpTrigger.getBoundingClientRect().top < (window.innerHeight * 0.4);
      }
      /* PDP without a USP block falls through to the 30% rule so the button
         doesn't appear immediately on those pages either. */
      return thirtyPercentScrolled();
    }

    var rafPending = false;
    function onScroll() {
      if (rafPending) return;
      rafPending = true;
      requestAnimationFrame(function () {
        rafPending = false;
        setVisible(computeNext());
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    setVisible(computeNext());
  }

  function waitForStellaFab(cb, attempts) {
    attempts = attempts || 0;
    var el = document.querySelector('stella-floating-action-button');
    if (el && el.shadowRoot) { cb(el, el.shadowRoot); return; }
    if (attempts > 200) return; // ~20s give up
    setTimeout(function () { waitForStellaFab(cb, attempts + 1); }, 100);
  }

  function init() {
    waitForStellaFab(function (hostEl, root) {
      injectShadowStyles(root);
      ensureCloseButton(root, hostEl);
      restoreMinimizedState(hostEl);

      /* Re-apply styles + close button if Stella re-renders its shadow tree. */
      var observer = new MutationObserver(function () {
        if (!root.querySelector('[data-jbl-stella-style]')) injectShadowStyles(root);
        ensureCloseButton(root, hostEl);
      });
      observer.observe(root, { childList: true, subtree: true });

      setupVisibility(hostEl);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
